import React, { useEffect, useState } from "react";
import Sidebar from "./Sidebar";
import { Search, Bell, ChevronRight } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { getRouteMeta } from "@/config/nav";

export default function AppLayout({ children, title, subtitle, actions }) {
  const { user } = useAuth();
  const branding = useBranding();
  const navigate = useNavigate();
  const location = useLocation();
  const meta = getRouteMeta(location.pathname);
  const [noteCount, setNoteCount] = useState(0);
  useEffect(() => { api.get("/notifications").then((r) => setNoteCount(r.data.count || 0)).catch(() => {}); }, []);
  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <header
          className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-slate-200"
          data-testid="app-header"
        >
          <div className="flex items-center justify-between px-6 py-3 gap-4">
            <div className="min-w-0">
              {meta && (
                <nav className="flex items-center gap-1 text-xs text-slate-400 mb-0.5" data-testid="header-breadcrumb" aria-label="Breadcrumb">
                  {meta.group && (<><span>{meta.group}</span><ChevronRight className="h-3 w-3" /></>)}
                  <span className="text-slate-600 font-medium" data-testid="header-breadcrumb-current">{meta.label}</span>
                </nav>
              )}
              <div className="label-cap text-slate-500" data-testid="header-company-label">{branding.name} O&M · {user?.role?.replace("_", " ")}</div>
              <h1 className="text-xl md:text-2xl font-bold tracking-tight text-slate-900 truncate" style={{ fontFamily: "Chivo" }}>
                {title}
              </h1>
              {subtitle && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
            </div>
            <div className="flex items-center gap-3">
              <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-md border border-slate-200 text-sm text-slate-500 w-72">
                <Search className="h-3.5 w-3.5" />
                <input
                  placeholder="Search…"
                  data-testid="header-search-input"
                  className="bg-transparent outline-none flex-1 text-slate-700 placeholder:text-slate-400"
                />
              </div>
              <button className="relative p-2 hover:bg-slate-100 rounded-md" data-testid="header-bell-button" onClick={() => navigate("/notifications")}>
                <Bell className="h-4 w-4 text-slate-600" />
                {noteCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="header-bell-count">{noteCount}</span>
                )}
              </button>
              {actions}
            </div>
          </div>
        </header>
        <main className="flex-1 p-6">{children}</main>
        <footer className="px-6 py-4 border-t border-slate-200 bg-white text-center text-xs text-slate-400" data-testid="app-footer">
          © {new Date().getFullYear()} {branding.name} · All Rights Reserved.
        </footer>
      </div>
    </div>
  );
}
